import { COMMAND_CENTER_ROOT } from '../../commandCenterRoutes'
import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import OliviumSuiteShell from './OliviumSuiteShell'
import GovToaster from './GovToaster'
import DurableSourceBanner from './DurableSourceBanner'
import { useGovernance } from './GovernanceProvider'
import { MetricTile, StatusPill, SavedStatusBadge, SeverityDot, formatWhen, STATUS_KIND_VAR } from './governanceUi'
import { GOV_MODULE_DEFS, GOV_MODULES, OLV_SUITE_ROOT, type GovModule } from './governanceCards'
import type { StatusKind, Severity, GovRecord } from './governanceModel'

export interface GovModuleWorkspaceProps {
  module: GovModule
  onOpenRecord: (module: GovModule, recordId: string) => void
  onOpenPath: (to: string) => void
  onHome: () => void
}

const KIND_LABEL: Record<StatusKind, string> = {
  verified: 'Verified', attention: 'Attention', denied: 'Denied',
  neutral: 'Neutral', identity: 'Recorded', secure: 'Secured', structure: 'Linked',
}

const HEALTH_VAR: Record<string, string> = {
  healthy: 'var(--govv-verified)', attention: 'var(--govv-attention)', degraded: 'var(--govv-denied)',
}

const inputStyle: CSSProperties = {
  padding: '8px 10px', fontSize: 13, borderRadius: 'var(--sc-radius-sm)',
  border: '1px solid var(--govv-line)', background: 'var(--sc-surface-2)', color: 'var(--govv-ink)',
}

/**
 * GovModuleWorkspace — a governance card's list view. Click-to-filter metric
 * tiles, a search bar, an honest create flow, and clickable record rows that
 * open the Universal Record Workspace. Every record is reachable and operable;
 * nothing is a dead tile.
 */
export default function GovModuleWorkspace({ module, onOpenRecord, onOpenPath, onHome }: GovModuleWorkspaceProps) {
  const gov = useGovernance()
  const [filterKind, setFilterKind] = useState<StatusKind | null>(null)
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [severity, setSeverity] = useState<Severity>('medium')

  const def = GOV_MODULE_DEFS[module]
  const metrics = gov.metrics(module)
  const health = gov.health(module)
  const canCreate = gov.can('create')

  const records = gov.byModule(module)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return records
      .filter((r) => (filterKind ? r.statusKind === filterKind : true))
      .filter((r) => (q ? (r.title + r.summary + Object.values(r.fields).join(' ')).toLowerCase().includes(q) : true))
      .slice()
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  }, [records, filterKind, query])

  const kindsPresent = (Object.keys(metrics.byKind) as StatusKind[]).filter((k) => metrics.byKind[k] > 0)

  const submitCreate = () => {
    if (!title.trim()) return
    gov.run({ type: 'create', input: { module, title: title.trim(), summary: summary.trim() || 'Created in the governance workspace', statusKind: module === 'permission-review' || module === 'risks' ? 'attention' : 'identity', severity } })
    setTitle(''); setSummary(''); setCreating(false)
  }

  return (
    <OliviumSuiteShell
      title={def.title}
      subtitle={def.blurb}
      accent={def.accent}
      honesty={def.honesty}
      breadcrumb={[
        { label: 'Command Center', to: COMMAND_CENTER_ROOT },
        { label: 'Olivium', to: OLV_SUITE_ROOT },
        { label: def.title },
      ]}
      onHome={onHome}
      onOpenPath={onOpenPath}
      onBack={() => onOpenPath(OLV_SUITE_ROOT)}
      rightSlot={<span className="olv-gov-pill" style={{ color: HEALTH_VAR[health] }}>{health}</span>}
    >
      <DurableSourceBanner />

      {/* Metric tiles (click to filter) */}
      <div className="olv-gov-metrics" style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(kindsPresent.length + 1, 6)}, 1fr)`, gap: 10, marginBottom: 16 }}>
        <MetricTile label="Total" value={metrics.total} varName="var(--govv-identity)" active={filterKind === null} onClick={() => setFilterKind(null)} />
        {kindsPresent.map((k) => (
          <MetricTile key={k} label={KIND_LABEL[k]} value={metrics.byKind[k]} varName={STATUS_KIND_VAR[k]} active={filterKind === k} onClick={() => setFilterKind(filterKind === k ? null : k)} />
        ))}
      </div>

      {/* Filter + create bar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input aria-label={`Search ${def.title}`} placeholder={`Search ${def.recordNoun}s…`} value={query} onChange={(e) => setQuery(e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: 200 }} />
        {canCreate.allowed ? (
          <button type="button" className="olv-gov-btn" data-variant="primary" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : `New ${def.recordNoun}`}
          </button>
        ) : (
          <span className="olv-gov-pill" style={{ color: 'var(--govv-ink-3)' }} title={canCreate.reason}>Create locked</span>
        )}
      </div>

      {creating && canCreate.allowed && (
        <div className="olv-gov-card" style={{ padding: 16, marginBottom: 16, ['--govv-accent' as string]: STATUS_KIND_VAR['attention'] }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>New {def.recordNoun}</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            <input aria-label="Title" placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} />
            <input aria-label="Summary" placeholder="Summary" value={summary} onChange={(e) => setSummary(e.target.value)} style={inputStyle} />
            <select aria-label="Severity" value={severity} onChange={(e) => setSeverity(e.target.value as Severity)} style={inputStyle}>
              {(['info', 'low', 'medium', 'high', 'critical'] as Severity[]).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div>
              <button type="button" className="olv-gov-btn" data-variant="primary" onClick={submitCreate} disabled={!title.trim()}>Create (local session)</button>
              <span style={{ marginLeft: 10, fontSize: 11, color: 'var(--govv-ink-3)' }}>Saved to this browser session and audited in the local ledger.</span>
            </div>
          </div>
        </div>
      )}

      {/* Records */}
      <div style={{ border: '1px solid var(--govv-line)', borderRadius: 'var(--govv-radius-record)', overflow: 'hidden' }}>
        {filtered.length === 0 && (
          <p style={{ padding: 20, color: 'var(--govv-ink-3)', fontSize: 13, margin: 0 }}>
            No {def.recordNoun}s match. {filterKind || query ? 'Clear the filter to see all.' : ''}
          </p>
        )}
        {filtered.map((r) => <RecordRow key={r.id} rec={r} onOpen={() => onOpenRecord(module, r.id)} />)}
      </div>

      <GovToaster />
    </OliviumSuiteShell>
  )
}

function RecordRow({ rec, onOpen }: { rec: GovRecord; onOpen: () => void }) {
  return (
    <button type="button" className="olv-gov-row" onClick={onOpen}
      style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 10, width: '100%', textAlign: 'left', padding: '12px 14px', border: 'none', borderBottom: '1px solid var(--govv-line)', background: 'transparent', cursor: 'pointer', alignItems: 'center' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <SeverityDot severity={rec.severity} />
          <span style={{ fontSize: 14, fontWeight: 600, color: STATUS_KIND_VAR[rec.statusKind], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rec.title}</span>
        </div>
        <span style={{ fontSize: 12, color: 'var(--govv-ink-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{rec.summary}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <StatusPill kind={rec.statusKind} label={rec.status} />
        <SavedStatusBadge status={rec.savedStatus} />
        <span style={{ fontSize: 11, color: 'var(--govv-ink-3)', fontFamily: 'var(--sc-font-mono)' }}>{formatWhen(rec.updatedAt)}</span>
      </div>
    </button>
  )
}
